// src/pages/reports/ReportBuilder.tsx
import React, { useState, useEffect } from 'react';
import {
  FileText, Download, BarChart3, PieChart as PieChartIcon,
  Table, Calendar, Building2, Tag, Target,
  Play, FileSpreadsheet, Printer, AlertCircle,
} from 'lucide-react';
import { Button, Card, Breadcrumb, Select } from '../../components/ui';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  PieChart, Pie, Cell, Legend, LineChart, Line,
} from 'recharts';
import { Navigate } from 'react-router-dom';
import { useRole } from '../../hooks/useRole';
import { useAuthStore } from '../../store/authStore';
import { reportService } from '../../services/report';
import toast from 'react-hot-toast';

type ReportType = 'laporan_status' | 'laporan_kategori' | 'laporan_unit' | 'laporan_risiko' | 'ppepp_progress' | 'kinerja_unit' | 'tren_bulanan' | 'overdue';
type ChartType = 'bar' | 'pie' | 'line' | 'table';

interface ReportConfig {
  type: ReportType;
  label: string;
  description: string;
  icon: React.ReactNode;
  supportedCharts: ChartType[];
}

interface Periode {
  id: number;
  nama: string;
  semester: number;
  tahun_ajaran: string;
}

interface Unit {
  id: number;
  name: string;
  code: string;
  level: number;
  path?: string;
}

const reportConfigs: ReportConfig[] = [
  { type: 'laporan_status', label: 'Laporan per Status', description: 'Distribusi laporan berdasarkan status workflow', icon: <FileText className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'laporan_kategori', label: 'Laporan per Kategori', description: 'Distribusi laporan berdasarkan kategori masalah', icon: <Tag className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'laporan_unit', label: 'Laporan per Unit', description: 'Jumlah laporan berdasarkan unit organisasi', icon: <Building2 className="w-5 h-5" />, supportedCharts: ['bar', 'table'] },
  { type: 'laporan_risiko', label: 'Distribusi Risiko', description: 'Sebaran tingkat risiko masalah', icon: <Target className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'ppepp_progress', label: 'Progress PPEPP', description: 'Status pelaksanaan rekomendasi RAPIM', icon: <BarChart3 className="w-5 h-5" />, supportedCharts: ['bar', 'pie', 'table'] },
  { type: 'kinerja_unit', label: 'Kinerja Unit', description: 'Perbandingan kinerja penyelesaian antar unit', icon: <Building2 className="w-5 h-5" />, supportedCharts: ['bar', 'table'] },
  { type: 'tren_bulanan', label: 'Tren Bulanan', description: 'Tren jumlah laporan per bulan', icon: <Calendar className="w-5 h-5" />, supportedCharts: ['line', 'bar', 'table'] },
  { type: 'overdue', label: 'Laporan Overdue', description: 'Daftar laporan yang melewati target penyelesaian', icon: <FileText className="w-5 h-5" />, supportedCharts: ['table'] },
];

const COLORS = ['#0f33ab', '#32B8C6', '#22C55E', '#F59E0B', '#EF4444', '#8B5CF6', '#F97316', '#06B6D4', '#6B7280', '#84CC16'];

const ReportBuilder: React.FC = () => {
  const role = useRole();
  const { user } = useAuthStore();

  const [selectedReport, setSelectedReport] = useState<ReportType | null>(null);
  const [chartType, setChartType] = useState<ChartType>('bar');
  const [periode, setPeriode] = useState<string>('all');
  const [unitId, setUnitId] = useState<string>('');
  const [generated, setGenerated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState<any[]>([]);
  const [periodes, setPeriodes] = useState<Periode[]>([]);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loadingFilters, setLoadingFilters] = useState(true);

  const currentConfig = reportConfigs.find(r => r.type === selectedReport);

  useEffect(() => {
    fetchFilters();
  }, []);

  // ========== AUTO-FILTER UNIT UNTUK DEKAN/KALEM ==========
  useEffect(() => {
    if ((role.isDekan || role.isKalem) && user?.unit?.id && !unitId) {
      setUnitId(String(user.unit.id));
    }
  }, [role, user, unitId]);

  const fetchFilters = async () => {
    try {
      const response = await reportService.getFilters();
      let filteredUnits = response.data.units || [];

      // Dekan/Kalem hanya lihat unit sendiri + child units
      if ((role.isDekan || role.isKalem) && user?.unit) {
        const userUnitPath = user.unit.path;
        filteredUnits = filteredUnits.filter((u: Unit) =>
          u.path === userUnitPath ||
          u.path?.startsWith(userUnitPath + '/')
        );
      }

      setPeriodes(response.data.periodes || []);
      setUnits(filteredUnits);
    } catch (error) {
      console.error('Error fetching filters:', error);
      toast.error('Gagal memuat data filter');
    } finally {
      setLoadingFilters(false);
    }
  };

  // ========== ACCESS CONTROL ==========
  if (!role.canAccessReportBuilder) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleGenerate = async () => {
    if (!selectedReport) {
      toast.error('Pilih jenis laporan terlebih dahulu');
      return;
    }
    setLoading(true);
    setGenerated(false);

    try {
      const response = await reportService.getReport(selectedReport, {
        periode_id: periode,
        unit_id: unitId
      });
      setReportData(response.data || []);
      setGenerated(true);
      toast.success('Laporan berhasil di-generate');
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error('Gagal generate laporan');
      setReportData([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExport = async (format: 'pdf' | 'excel') => {
    if (!selectedReport) return;
    const toastId = toast.loading(`Mengekspor sebagai ${format.toUpperCase()}...`);
    try {
      const fileData = await reportService.exportReport(selectedReport, format, {
        periode_id: periode,
        unit_id: unitId
      });

      const blob = new Blob([fileData], { type: format === 'pdf' ? 'application/pdf' : 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `laporan_${selectedReport}_${new Date().getTime()}.${format === 'excel' ? 'csv' : 'pdf'}`);
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Export berhasil', { id: toastId });
    } catch (error: any) {
      console.error('Export failed:', error);
      if (error.response?.status === 501) {
        toast.error('Fitur export belum tersedia', { id: toastId });
      } else if (error.message) {
        toast.error(error.message, { id: toastId });
      } else {
        toast.error('Gagal export laporan', { id: toastId });
      }
    }
  };

  const renderChart = () => {
    if (!selectedReport || !generated) return null;

    if (chartType === 'table' || selectedReport === 'overdue') {
      return renderTable();
    }

    if (reportData.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <AlertCircle className="w-12 h-12 text-neutral-border mb-3" />
          <p className="text-sm text-neutral-text-secondary">Tidak ada data untuk ditampilkan</p>
        </div>
      );
    }

    if (chartType === 'pie') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <PieChart>
            <Pie
              data={reportData}
              cx="50%"
              cy="50%"
              innerRadius={80}
              outerRadius={150}
              paddingAngle={3}
              dataKey="value"
              label={({ name, percent }: { name?: string, percent?: number }) => `${name ?? ''} (${((percent ?? 0) * 100).toFixed(0)}%)`}
            >
              {reportData.map((entry: any, index: number) => (
                <Cell key={`cell-${index}`} fill={entry.fill || COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      );
    }

    if (chartType === 'line') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={reportData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
            <Line type="monotone" dataKey="value" stroke="#0f33ab" strokeWidth={3} dot={{ fill: '#0f33ab', r: 5 }} />
          </LineChart>
        </ResponsiveContainer>
      );
    }

    if (selectedReport === 'kinerja_unit') {
      return (
        <ResponsiveContainer width="100%" height={400}>
          <BarChart data={reportData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={60} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
            <Legend />
            <Bar dataKey="selesai" fill="#22C55E" name="Selesai" radius={[4, 4, 0, 0]} />
            <Bar dataKey="proses" fill="#F59E0B" name="Proses" radius={[4, 4, 0, 0]} />
            <Bar dataKey="overdue" fill="#EF4444" name="Overdue" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      );
    }

    return (
      <ResponsiveContainer width="100%" height={400}>
        <BarChart data={reportData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
          <XAxis dataKey="name" tick={{ fontSize: 12 }} angle={-15} textAnchor="end" height={60} />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip contentStyle={{ borderRadius: '12px', fontSize: '12px', border: '1px solid #E5E7EB' }} />
          <Bar dataKey="value" radius={[6, 6, 0, 0]}>
            {reportData.map((entry: any, index: number) => (
              <Cell key={`cell-${index}`} fill={entry.fill || COLORS[index % COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  };

  const renderTable = () => {
    if (selectedReport === 'overdue') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-bg">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Kode</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Judul</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Unit</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Risiko</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Terlambat</th>
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">PIC</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length === 0 ? (
                <tr>
                  <td colSpan={6} className="text-center py-8 text-neutral-text-secondary">Tidak ada data overdue</td>
                </tr>
              ) : (
                reportData.map((row, i) => (
                  <tr key={i} className="border-b border-neutral-border hover:bg-danger/5">
                    <td className="px-4 py-3 text-xs font-mono text-teal-main">{row.kode}</td>
                    <td className="px-4 py-3 text-sm font-medium">{row.judul}</td>
                    <td className="px-4 py-3 text-sm text-neutral-text-secondary">{row.unit}</td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${row.risiko === 'Kritis' ? 'bg-red-100 text-red-700' :
                        row.risiko === 'Tinggi' ? 'bg-orange-100 text-orange-700' :
                          row.risiko === 'Sedang' ? 'bg-yellow-100 text-yellow-700' : 'bg-green-100 text-green-700'
                        }`}>{row.risiko}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-bold text-danger">{row.hari} hari</td>
                    <td className="px-4 py-3 text-sm text-neutral-text-secondary">{row.pic}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    if (selectedReport === 'kinerja_unit') {
      return (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-neutral-bg">
                <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Unit</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Selesai</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Proses</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Overdue</th>
                <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Total</th>
              </tr>
            </thead>
            <tbody>
              {reportData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-neutral-text-secondary">Tidak ada data</td>
                </tr>
              ) : (
                reportData.map((row: any, i: number) => (
                  <tr key={i} className="border-b border-neutral-border">
                    <td className="px-4 py-3 text-sm font-medium">{row.name}</td>
                    <td className="px-4 py-3 text-sm text-right text-success font-semibold">{row.selesai}</td>
                    <td className="px-4 py-3 text-sm text-right text-warning font-semibold">{row.proses}</td>
                    <td className="px-4 py-3 text-sm text-right text-danger font-semibold">{row.overdue}</td>
                    <td className="px-4 py-3 text-sm text-right font-bold">{row.selesai + row.proses + row.overdue}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      );
    }

    const total = reportData.reduce((sum: number, r: any) => sum + (r.value || 0), 0);

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-bg">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">No</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Nama</th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Jumlah</th>
              <th className="text-right px-4 py-3 text-xs font-semibold uppercase text-neutral-text-secondary">Persentase</th>
            </tr>
          </thead>
          <tbody>
            {reportData.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-center py-8 text-neutral-text-secondary">Tidak ada data</td>
              </tr>
            ) : (
              <>
                {reportData.map((row: any, i: number) => (
                  <tr key={i} className="border-b border-neutral-border">
                    <td className="px-4 py-3 text-sm text-neutral-text-secondary">{i + 1}</td>
                    <td className="px-4 py-3 text-sm font-medium flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: row.fill || COLORS[i % COLORS.length] }} />
                      {row.name}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold">{row.value}</td>
                    <td className="px-4 py-3 text-sm text-right text-neutral-text-secondary">{total > 0 ? ((row.value / total) * 100).toFixed(1) : 0}%</td>
                  </tr>
                ))}
                <tr className="bg-neutral-bg font-bold">
                  <td className="px-4 py-3 text-sm" colSpan={2}>Total</td>
                  <td className="px-4 py-3 text-sm text-right">{total}</td>
                  <td className="px-4 py-3 text-sm text-right">100%</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  const periodeOptions = [
    { value: 'all', label: 'Semua Periode' },
    ...periodes.map(p => ({ value: String(p.id), label: p.nama }))
  ];

  const unitOptions = [
    { value: '', label: 'Semua Unit' },
    ...units.map(u => ({ value: String(u.id), label: u.name }))
  ];

  return (
    <div className="space-y-6 animate-in">
      <Breadcrumb items={[
        { label: 'Dashboard', href: '/dashboard' },
        { label: 'Laporan & Analitik' },
      ]} />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-black text-teal-dark tracking-tight">Laporan & Analitik</h1>
          <p className="text-sm text-neutral-text-secondary mt-1">Buat laporan dengan visualisasi data dinamis</p>
        </div>
      </div>

      {/* Info Banner untuk Dekan/Kalem */}
      {(role.isDekan || role.isKalem) && (
        <div className="p-3 bg-info/5 border border-info/20 rounded-xl flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-info shrink-0" />
          <p className="text-xs text-info">
            Anda hanya dapat melihat data untuk <strong>{user?.unit?.name}</strong> dan unit di bawahnya.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-4">
          <Card>
            <h3 className="text-sm font-bold text-teal-dark mb-3">1. Pilih Jenis Laporan</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {reportConfigs.map((config) => (
                <button
                  key={config.type}
                  onClick={() => {
                    setSelectedReport(config.type);
                    setGenerated(false);
                    setChartType(config.supportedCharts[0]);
                  }}
                  className={`w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all ${selectedReport === config.type
                    ? 'bg-teal-main/5 border-2 border-teal-main'
                    : 'border-2 border-transparent hover:bg-neutral-bg'
                    }`}
                >
                  <div className={`p-2 rounded-lg shrink-0 ${selectedReport === config.type ? 'bg-teal-main text-white' : 'bg-neutral-bg text-neutral-text-secondary'
                    }`}>
                    {config.icon}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-neutral-text-primary">{config.label}</p>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">{config.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </Card>

          <Card>
            <h3 className="text-sm font-bold text-teal-dark mb-3">2. Filter</h3>
            {loadingFilters ? (
              <p className="text-sm text-neutral-text-secondary">Memuat filter...</p>
            ) : (
              <div className="space-y-3">
                <Select
                  label="Periode"
                  options={periodeOptions}
                  value={periode}
                  onChange={setPeriode}
                />
                <Select
                  label="Unit (Opsional)"
                  options={unitOptions}
                  value={unitId}
                  onChange={setUnitId}
                />
              </div>
            )}
          </Card>

          {selectedReport && currentConfig && (
            <Card>
              <h3 className="text-sm font-bold text-teal-dark mb-3">3. Tampilan</h3>
              <div className="grid grid-cols-2 gap-2">
                {currentConfig.supportedCharts.map((ct) => (
                  <button
                    key={ct}
                    onClick={() => setChartType(ct)}
                    className={`flex items-center gap-2 p-2.5 rounded-lg text-sm font-medium transition-all ${chartType === ct
                      ? 'bg-teal-main text-white'
                      : 'bg-neutral-bg text-neutral-text-secondary hover:bg-neutral-border'
                      }`}
                  >
                    {ct === 'bar' && <BarChart3 className="w-4 h-4" />}
                    {ct === 'pie' && <PieChartIcon className="w-4 h-4" />}
                    {ct === 'line' && <BarChart3 className="w-4 h-4" />}
                    {ct === 'table' && <Table className="w-4 h-4" />}
                    {ct === 'bar' ? 'Batang' : ct === 'pie' ? 'Donat' : ct === 'line' ? 'Garis' : 'Tabel'}
                  </button>
                ))}
              </div>
            </Card>
          )}

          <Button
            variant="primary"
            className="w-full"
            onClick={handleGenerate}
            icon={<Play className="w-4 h-4" />}
            disabled={!selectedReport}
            loading={loading}
          >
            Generate Laporan
          </Button>
        </div>

        <div className="lg:col-span-2">
          {!generated ? (
            <Card className="flex flex-col items-center justify-center py-20 text-center">
              <BarChart3 className="w-16 h-16 text-neutral-border mb-4" />
              <h3 className="text-lg font-bold text-neutral-text-primary">Pilih & Generate Laporan</h3>
              <p className="text-sm text-neutral-text-secondary mt-1 max-w-md">
                Pilih jenis laporan di panel kiri, atur filter, lalu klik "Generate Laporan" untuk melihat hasil.
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              <Card>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h2 className="text-lg font-bold text-teal-dark">{currentConfig?.label}</h2>
                    <p className="text-xs text-neutral-text-secondary mt-0.5">
                      Di-generate: {new Date().toLocaleString('id-ID')}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleExport('excel')} icon={<FileSpreadsheet className="w-4 h-4" />}>
                      Excel
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} icon={<Download className="w-4 h-4" />}>
                      PDF
                    </Button>
                    <Button variant="ghost" size="sm" onClick={handlePrint} icon={<Printer className="w-4 h-4" />}>
                      Print
                    </Button>
                  </div>
                </div>

                <div className="min-h-[400px]">
                  {renderChart()}
                </div>
              </Card>

              {selectedReport !== 'overdue' && chartType !== 'table' && (
                <Card padding="sm">
                  <h3 className="text-sm font-bold text-teal-dark mb-3">Ringkasan Data</h3>
                  {renderTable()}
                </Card>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReportBuilder;
